import type { Gender } from "src/types/product-listing";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import {
  getPathByTypeAndGender,
  toAbsoluteUrl,
} from "../../../utils/getBaseUrl";

/**
 * Returns the absolute URL of the genderless brand page.
 *
 * A Brand entity has a single canonical URL — the gender segment belongs to the
 * page, not the brand.
 *
 * @param brandSlug - The URL slug of the brand.
 * @param locale - The locale to use for the URL.
 * @returns The absolute URL string for the brand page or undefined if the URL cannot be generated.
 */
export const getBrandUrl = (
  brandSlug: string,
  locale: string
): string | undefined => {
  if (!brandSlug) {
    return undefined;
  }

  const brandPath = getPathByTypeAndGender({
    type: "brand",
    slug: brandSlug,
    locale,
  });
  if (brandPath) {
    return toAbsoluteUrl(brandPath);
  }
  return undefined;
};

/**
 * Returns the localized gender slug to use as a URL segment, or undefined when
 * the page is genderless (no gender, or ShopType.All).
 */
export const getGenderSegment = (gender?: Gender): string | undefined => {
  if (!gender) {
    return undefined;
  }

  const genderToLower = gender.toLowerCase();

  return genderToLower === ShopType.All ? undefined : genderToLower;
};
